import { useEffect, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { SWReactIcons } from "@similarweb/icons";
import { Button, IconButton, PixelPlaceholderLoader, TabList, TabPanel, Tabs } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { createMultiIncludeExcludeFilterFromItems } from "app/pages/website-analysis/traffic-sources/search/components/filters/MultiIncludeExclude/utils";
import { allTrackers } from "app/services/track/track";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { WebsiteKeywordsPage2Graph } from "../../website-keywords/table/clicks-over-time-graph/WebsiteKeywordsPage2Graph";
import { useEnrichedKeywordsData } from "../hooks/useEnrichedKeywordsData";
import { CloseButtonWrapper, KeywordsTableWrapper, TabStyled, TabText } from "../styleComponents";
import { useTopicsNavigationTargets } from "../topicsNavigationTargets";
import type { ITopicsEnrichedContainerProps, ITransformedEnrichedKeywordsData, ColumnGenerator } from "../types";
import { EnrichedTabs } from "../types";

import { getEnrichedKeywordsTableColumns } from "./enrichedColumnsConfig";
import { EnrichedTabBase } from "./EnrichedTabBase";
import { initialTableSort } from "./EnrichedTable";
import { KeywordGroupCreationModal } from "./KeywordGroupCreationModal";
import { useKeywordGroupCreation } from "./useKeywordGroupCreation";

export const TopicsEnrichedContainer = ({
    row,
    expandedRowTabId,
    onClose,
    showKeywordGroupButton = true,
}: ITopicsEnrichedContainerProps) => {
    const [selectedTab, setSelectedTab] = useState<EnrichedTabs>(expandedRowTabId);
    const [tabPages, setTabPages] = useState<Record<EnrichedTabs, number>>({
        [EnrichedTabs.Keywords]: 1,
        [EnrichedTabs.Trends]: 1,
    });
    const [tabSorts, setTabSorts] = useState<Record<EnrichedTabs, typeof initialTableSort>>({
        [EnrichedTabs.Keywords]: initialTableSort,
        [EnrichedTabs.Trends]: initialTableSort,
    });
    const { params } = useCurrentStateAndParams();
    const { topUrlTargetState } = useTopicsNavigationTargets();

    const {
        data: keywordsData,
        isLoading: isKeywordsLoading,
        isError: isKeywordsError,
    } = useEnrichedKeywordsData(row.TopicId, row.Keywords, tabSorts[EnrichedTabs.Keywords]);

    const {
        isOpen,
        isLoading,
        errorMessage,
        openModal,
        closeModal,
        handleCreateGroup,
        handleViewGroup,
        handleStartOver,
    } = useKeywordGroupCreation({
        keywords: keywordsData?.map((item) => item.Keyword) || [],
        defaultGroupName: row.Topic,
        trackingContext: row.Topic,
        targetState: "organicsearch_keywordAnalysis_overview_2",
    });

    const getFilterItems = (keywords: string[]): string => {
        return createMultiIncludeExcludeFilterFromItems(
            keywords.map((keyword) => ({
                type: "keyword",
                value: keyword,
            })),
            "keyword",
        );
    };

    const onTabSelect = (index: EnrichedTabs) => {
        setSelectedTab(index);
        allTrackers.dispatchTrackEvent("Tab", "click", `Enriched/${EnrichedTabs[index]}`);
    };

    const handlePageChange = (tab: EnrichedTabs, page: number) => {
        setTabPages((prev) => ({ ...prev, [tab]: page }));
    };

    const handleSortChange = (tab: EnrichedTabs, sort: typeof initialTableSort) => {
        setTabSorts((prev) => ({ ...prev, [tab]: sort }));
        setTabPages((prev) => ({ ...prev, [tab]: 1 })); // Reset page when sort changes
    };

    const handleCloseExpandedRow = () => {
        allTrackers.dispatchTrackEvent("Table", "Collapse", `Close keyword expansion`);
        onClose();
        document.body.click();
    };

    const handleKeywordsNavigation = () => {
        allTrackers.dispatchTrackEvent("Internal Link", "click", "Topics Drilldown/Keywords");
        const keywords = keywordsData?.map((item) => item.Keyword) || [];

        void proNavigator.go("organicsearch_websites_v2_traffic_keywords", {
            ...params,
            multiIncludeExcludeKeywords: getFilterItems(keywords),
        });
    };

    const keywordsBottomSection = (
        <Button onClick={openModal}>
            {translate("website.topics.table.enriched.tab.keywords.bottom.button.title")}
        </Button>
    );

    const enrichedColumnGenerator: ColumnGenerator = (currentPage, pageSize, selectedPageTab, sortConfig) =>
        getEnrichedKeywordsTableColumns(currentPage, pageSize, selectedPageTab, sortConfig, topUrlTargetState);

    useEffect(() => {
        setSelectedTab(expandedRowTabId);
    }, [expandedRowTabId]);
    return (
        <KeywordsTableWrapper>
            <Tabs
                onSelect={onTabSelect}
                selectedIndex={selectedTab}
            >
                <TabList>
                    <TabStyled id={EnrichedTabs.Keywords}>
                        <SWReactIcons
                            iconName="search-keywords"
                            size="xs"
                        />
                        <TabText>{translate("website.topics.table.enriched.tab.keywords.title")}</TabText>
                    </TabStyled>
                    <TabStyled id={EnrichedTabs.Trends}>
                        <SWReactIcons
                            iconName="trend-up"
                            size="xs"
                        />
                        <TabText>{translate("website.topics.table.enriched.tab.trends.title")}</TabText>
                    </TabStyled>
                </TabList>

                <TabPanel>
                    <EnrichedTabBase<ITransformedEnrichedKeywordsData>
                        selectedPageTab={EnrichedTabs[expandedRowTabId]}
                        onLinkClick={handleKeywordsNavigation}
                        getTableColumns={enrichedColumnGenerator}
                        bottomSection={showKeywordGroupButton ? keywordsBottomSection : undefined}
                        linkButtonTitle={translate("website.topics.table.enriched.tab.keywords.top.link.title")}
                        topTitle={translate("website.topics.table.enriched.tab.keywords.top.title", {
                            topic: row.Topic,
                        })}
                        data={keywordsData ?? []}
                        currentPage={tabPages[EnrichedTabs.Keywords]}
                        setCurrentPage={(page) => handlePageChange(EnrichedTabs.Keywords, page)}
                        isLoading={isKeywordsLoading}
                        isError={isKeywordsError}
                        totalCount={row.Keywords}
                        sort={tabSorts[EnrichedTabs.Keywords]}
                        onSort={(sort) => handleSortChange(EnrichedTabs.Keywords, sort)}
                        handleCloseExpandedRow={handleCloseExpandedRow}
                    />
                </TabPanel>
                <TabPanel>
                    <CloseButtonWrapper>
                        <IconButton
                            type="flat"
                            iconName="clear"
                            iconSize="xs"
                            onClick={handleCloseExpandedRow}
                        />
                    </CloseButtonWrapper>
                    {isKeywordsLoading ? (
                        <Flex
                            justifyContent="center"
                            alignItems="center"
                            padding="40px"
                            margin="35px 0 0 0"
                            min-height="200px"
                        >
                            <PixelPlaceholderLoader
                                width={"100%"}
                                height={250}
                            />
                        </Flex>
                    ) : (
                        <WebsiteKeywordsPage2Graph
                            selectedRowsKeywords={keywordsData?.map((item) => item.Keyword) || []}
                            shouldPresentLegend={false}
                            shouldPresentRefreshButton={false}
                        />
                    )}
                </TabPanel>
            </Tabs>

            <KeywordGroupCreationModal
                isOpen={isOpen}
                isLoading={isLoading}
                errorMessage={errorMessage}
                keywordsCount={row.Keywords}
                defaultGroupName={row.Topic}
                onClose={closeModal}
                onCreateGroup={handleCreateGroup}
                onViewGroup={handleViewGroup}
                onStartOver={handleStartOver}
            />
        </KeywordsTableWrapper>
    );
};
